import type {
  CellFocusedEvent,
  ColDef,
  ColumnEvent,
  ColumnState,
  GridApi,
  GridReadyEvent,
  RowDragEndEvent,
  RowDragEvent,
} from 'ag-grid-community'
import { BulkSheetView } from '../../../../containers/BulkSheetView'
import {
  defaultColDef as gridDefaultColDef,
  frameworkComponents,
} from '../../../../containers/commons/AgGrid'
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { VersionReportStatus } from '../../../../../domain/entity/version-report/VersionReportVersionEntity'
import { TeamEntity } from '../../../../../domain/entity/TeamEntity'
import styled from 'styled-components'
import { colorPalette } from '../../../../style/colorPalette'
import {
  CommentExpansionContext,
  CommentExpansionState,
} from '../cell/CommentCellRenderer'

export type ProgressReportTableContent = {
  uuid: string
  name: string
  status: VersionReportStatus
  comment: string
  scheduledProgressRate: string
  progressRate: string
  total: string
  planned: string
  completed: string
  delay: string
  remaining: string
  wbsItemStatus: string
  team?: TeamEntity
  progressRateDiff: string
  scheduledStartDate?: string
  scheduledEndDate?: string
  maxChildScheduledEndDate?: string
  preceding: string
  unplanned: string
}
export type Value = {
  workload: number
  count: number
}

const columnEventValidSources = new Set([
  'toolPanelUi',
  'uiColumnMoved',
  'uiColumnResized',
  'uiColumnDragged',
])

type ProgressReportTableProps = {
  editing: boolean
  columns: ColDef[]
  contents: ProgressReportTableContent[]
  context?: Record<string, unknown>
  onGridReady?: (event: GridReadyEvent) => void
  updateColumnStateConfig?: (state: ColumnState[]) => void
  onRowDragEnter?: (event: RowDragEvent) => void
  onRowDragMove?: (event: RowDragEvent) => void
  onRowDragLeave?: (event: RowDragEvent) => void
  onRowDragEnd?: (event: RowDragEndEvent) => void
  rowHeight?: number
  columnState?: ColumnState[]
}
export const ProgressReportTable = forwardRef<
  HTMLDivElement,
  ProgressReportTableProps
>(
  (
    {
      contents,
      editing,
      context,
      onGridReady,
      updateColumnStateConfig,
      onRowDragEnter,
      onRowDragMove,
      onRowDragLeave,
      onRowDragEnd,
      rowHeight,
      columnState,
      columns,
    }: ProgressReportTableProps,
    ref
  ) => {
    const [gridInitialized, setGridInitialized] = useState(false)
    const [expandedKey, setExpandedKey] = useState<string | null>(null)
    const gridApiRef = useRef<GridApi | null>(null)

    const expansionContextValue = useMemo<CommentExpansionState>(
      () => ({ expandedKey, setExpandedKey }),
      [expandedKey]
    )

    const defaultColDef = useMemo<ColDef>(
      () => gridDefaultColDef({ editable: false, sortable: true }),
      []
    )

    const gridOptions = useMemo(
      () => ({
        columnDefs: columns,
        defaultColDef,
        defaultColGroupDef: { suppressHeaderContextMenu: true },
        maintainColumnOrder: true,
      }),
      // 列定義は初期化時のみ適用し、再レンダーでの列幅リセットを防ぐ
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    )

    const onColumnChanged = useCallback(
      (e: ColumnEvent) => {
        if (!e.api) return
        if (!e.source || !columnEventValidSources.has(e.source)) return
        if ('finished' in e && e.finished === false) return
        const state = e.api.getColumnState()
        updateColumnStateConfig?.(state)
      },
      [updateColumnStateConfig]
    )

    const applyStoredColumnState = useCallback(() => {
      if (gridApiRef.current && columnState && columnState.length > 0) {
        gridApiRef.current.applyColumnState({
          state: columnState,
          applyOrder: true,
        })
        gridApiRef.current.moveColumns(['drag'], 0)
      }
    }, [columnState])
    const applyStoredColumnStateRef = useRef(applyStoredColumnState)
    useEffect(() => {
      applyStoredColumnStateRef.current = applyStoredColumnState
    }, [applyStoredColumnState])

    const handleGridReady = useCallback(
      (e: GridReadyEvent) => {
        gridApiRef.current = e.api
        applyStoredColumnStateRef.current()
        onGridReady?.(e)
        // for avoiding flickering, set a delay before showing the grid
        setTimeout(() => setGridInitialized(true), 200)
      },
      [onGridReady]
    )

    const onCellFocused = useCallback(
      (event: CellFocusedEvent) => {
        const { column } = event
        if (!column) return
        const colId = typeof column === 'string' ? column : column.getColId()
        // 定性コメント展開ボタン操作時に列が勝手に移動するのを防ぐ
        if (!editing && colId === 'comment') return
        event.api.ensureColumnVisible(column)
      },
      [editing]
    )

    useEffect(() => {
      const api = gridApiRef.current
      if (!api) return
      const currentState = api.getColumnState()
      api.setGridOption('columnDefs', columns)
      api.applyColumnState({ state: currentState, applyOrder: true })
      api.moveColumns(['drag'], 0)
      api.refreshHeader()
    }, [columns])

    return (
      <CommentExpansionContext.Provider value={expansionContextValue}>
        <HiddenUntilInitialized isVisible={gridInitialized}>
          <BulkSheetView
            ref={ref}
            gridOptions={gridOptions}
            rowData={contents}
            context={context}
            rowHeight={rowHeight}
            components={frameworkComponents}
            onGridReady={handleGridReady}
            suppressContextMenu={true}
            suppressMovableColumns={!editing}
            suppressMoveWhenRowDragging={!editing}
            suppressDragLeaveHidesColumns={!editing}
            rowDragManaged={false}
            suppressRowDrag={!editing}
            onRowDragEnter={onRowDragEnter}
            onRowDragMove={onRowDragMove}
            onRowDragLeave={onRowDragLeave}
            onRowDragEnd={onRowDragEnd}
            onColumnVisible={onColumnChanged}
            onColumnResized={onColumnChanged}
            onColumnMoved={onColumnChanged}
            enterNavigatesVertically={true}
            enterNavigatesVerticallyAfterEdit={true}
            onCellFocused={onCellFocused}
            processUnpinnedColumns={() => []}
            {...(!editing ? { sideBar: undefined } : {})}
          />
        </HiddenUntilInitialized>
      </CommentExpansionContext.Provider>
    )
  }
)

const HiddenUntilInitialized = styled('div')<{ isVisible: boolean }>(
  ({ isVisible }) => ({
    visibility: isVisible ? 'visible' : 'hidden',
    width: '100%',
    height: '100%',
    '& .ag-theme-balham': {
      '--ag-cell-horizontal-border': `1px dashed ${colorPalette.monotone[2]}`,
    },
    '& .ag-cell.comment-cell-expandable': {
      overflow: 'visible !important',
    },
    '& .ag-row.has-expanded-comment-cell': {
      overflow: 'visible !important',
      zIndex: 10,
      backgroundColor: `${colorPalette.skyBlue[1]} !important`,
    },
    '& .ag-row.has-expanded-comment-cell .ag-cell': {
      backgroundColor: `${colorPalette.skyBlue[1]} !important`,
    },
  })
)
